import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Scissors, Sparkles, Star, ScanFace, Droplet } from 'lucide-react';
import { services } from '../data/mock';
import { useScrollAnimation, useIndividualScrollAnimation } from '../hooks/useScrollAnimation';
import ServiceModal from './ServiceModal';

const iconMap = {
  scissors: Scissors,
  sparkles: Sparkles,
  scan: ScanFace,
  star: Star,
  droplet: Droplet
};

const ServiceCard = ({ service, serviceKey, t, onClick, size = 'md' }) => {
  const [ref, isVisible, scrollDirection] = useIndividualScrollAnimation(0.2);
  const IconComponent = iconMap[service.icon];
  const animationClass = scrollDirection === 'down' ? 'slide-in-left' : 'slide-in-right';
  const titleSize = size === 'sm' ? 'text-lg' : 'text-xl';
  const priceSize = size === 'sm' ? 'text-2xl' : 'text-3xl';

  return (
    <div
      ref={ref}
      data-testid={`service-card-${serviceKey}`}
      onClick={() => onClick(service, serviceKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(service, serviceKey);
        }
      }}
      role="button"
      tabIndex={0}
      className={`service-card-flip cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40 rounded-sm ${animationClass} ${isVisible ? 'visible' : ''}`}
    >
      <Card className="bg-neutral-900 border border-neutral-800 hover:border-white transition-all duration-300 group rounded-sm h-full">
        <CardHeader className="pb-4">
          <div className="mb-6 p-3 rounded-sm bg-black/50 w-fit">
            <IconComponent className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <CardTitle className={`text-white ${titleSize} font-oswald uppercase tracking-wide mb-3`}>
            {t(`servicesData.${serviceKey}.title`)}
          </CardTitle>
          <CardDescription className="text-gray-400 text-sm leading-relaxed font-montserrat font-light">
            {t(`servicesData.${serviceKey}.description`)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-baseline pt-6 border-t border-neutral-800">
            <span className={`${priceSize} font-oswald font-bold text-white`}>
              {service.price}
            </span>
            <span className="text-gray-500 text-sm font-montserrat">
              {service.duration}
            </span>
          </div>
          <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gray-500 group-hover:text-white transition-colors font-montserrat">
            <span>{t('servicesModal.viewDetails')}</span>
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Services = () => {
  const { t } = useTranslation();
  const [titleRef, titleVisible] = useScrollAnimation(0.1);
  const [badgeRef, badgeVisible] = useScrollAnimation(0.1);

  const [activeService, setActiveService] = useState(null);
  const [activeServiceKey, setActiveServiceKey] = useState(null);

  const serviceKeys = {
    1: 'cleanCut',
    2: 'signatureCut',
    3: 'prestigeCut',
    4: 'eyebrow',
    5: 'waxing',
    6: 'hairWash'
  };

  const handleCardClick = (service, serviceKey) => {
    setActiveService(service);
    setActiveServiceKey(serviceKey);
  };

  const handleClose = () => {
    setActiveService(null);
    setActiveServiceKey(null);
  };

  const cutServices = services.filter(s => s.category === 'cuts');
  const otherServices = services.filter(s => s.category !== 'cuts');

  return (
    <section id="services" className="py-20 sm:py-32 bg-black mt-12 sm:mt-0">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={titleRef}
          className={`mb-12 sm:mb-20 fade-in ${titleVisible ? 'visible' : ''}`}
        >
          <p className="text-white text-xs uppercase tracking-[0.3em] mb-4 font-montserrat font-medium">{t('services.badge')}</p>
          <h2 className="text-5xl md:text-6xl font-oswald font-bold text-white mb-6 uppercase tracking-tighter">
            {t('services.title')}
          </h2>
          <p className="text-gray-400 text-base max-w-3xl font-montserrat font-light">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Main Cut Services */}
        <div className="mb-20">
          <h3 className="text-2xl font-oswald font-medium text-white mb-12 uppercase tracking-wider border-b border-neutral-800 pb-4">
            {t('services.mainTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {cutServices.map((service) => {
              const serviceKey = serviceKeys[service.id];
              return (
                <ServiceCard
                  key={service.id}
                  service={service}
                  serviceKey={serviceKey}
                  t={t}
                  onClick={handleCardClick}
                  size="md"
                />
              );
            })}
          </div>
        </div>

        {/* Additional Services */}
        <div>
          <h3 className="text-2xl font-oswald font-medium text-white mb-12 uppercase tracking-wider border-b border-neutral-800 pb-4">
            {t('services.extrasTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {otherServices.map((service) => {
              const serviceKey = serviceKeys[service.id];
              return (
                <ServiceCard
                  key={service.id}
                  service={service}
                  serviceKey={serviceKey}
                  t={t}
                  onClick={handleCardClick}
                  size="sm"
                />
              );
            })}
          </div>
        </div>

        {/* Altegio Booking CTA */}
        <div
          ref={badgeRef}
          className={`mt-20 text-center slide-up ${badgeVisible ? 'visible' : ''}`}
        >
          <a
            href="https://n608855.alteg.io/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <div className="inline-block bg-black border-2 border-white rounded-sm px-12 py-6 hover:bg-white hover:text-black transition-all duration-300 cursor-pointer group">
              <p className="text-white font-oswald uppercase text-lg tracking-wider mb-1 group-hover:text-black transition-colors">{t('services.bookingCta')}</p>
              <p className="text-gray-400 text-sm font-montserrat group-hover:text-gray-600 transition-colors">{t('services.bookingCtaSub')}</p>
            </div>
          </a>
        </div>
      </div>

      {/* Service Detail Modal */}
      <ServiceModal
        service={activeService}
        serviceKey={activeServiceKey}
        isOpen={!!activeService}
        onClose={handleClose}
        t={t}
      />
    </section>
  );
};

export default Services;
